import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { debounce, isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../core/modules/global';
import { CREATE_BUSINESS_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import { useBusinessCore } from '../../../../core/modules/business';
import { DISTRICTS } from '../../../../core/constants/chileDistrict';
import { transformRUT, validateRUT } from '../../../../core/utils/utils';
import { ERROR } from '../../../../core/constants/errorCode';

const schema = yup.object().shape({
  name: yup.string().required('Campo requerido'),
  rut: yup
    .string()
    .required('Campo requerido')
    .test('test-rut', 'Rut inválido. Ej: 11.111.111-K', function (value) {
      return validateRUT(value);
    })
    .transform((v) => transformRUT(v)),
  acronym: yup
    .string()
    .test('len', '3 carácteres requeridos', (val) => !val || val.length === 3)
    .optional(),
  business_name: yup.string().required('Campo requerido'),
  district: yup.string().required('Campo requerido'),
  street: yup.string().required('Campo requerido'),
  number: yup.string().required('Campo requerido'),
  phone: yup.string().required('Campo requerido'),
  contact_name: yup.string().required('Campo requerido'),
  contact_email: yup
    .string()
    .email('Formato incorrecto')
    .required('Campo requerido'),
  unit: yup.string().optional(),
  comments: yup.string().optional(),
});

const CreateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { postBusiness, getBusinesses, errorCode, clearBusiness } = useBusinessCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading] = useState(false);

  // Limpiar error cuando se abre el diálogo
  useEffect(() => {
    if (Array.isArray(dialogs) && dialogs.includes(CREATE_BUSINESS_DIALOG)) {
      // Solo limpiar el error, no todo el business
      clearBusiness();
    }
  }, [dialogs]);

  const { control, formState, handleSubmit, resetField, setValue, watch } =
    useForm({
      mode: 'onBlur',
      resolver: yupResolver(schema),
    });
  const { isValid, errors, dirtyFields } = formState;

  // Observar los valores del formulario
  const watchedValues = watch();

  // Verificar si todos los campos requeridos están llenos
  const requiredFields: (keyof typeof watchedValues)[] = [
    'name',
    'rut',
    'business_name',
    'district',
    'street',
    'number',
    'phone',
    'contact_name',
    'contact_email'
  ];
  const allRequiredFieldsFilled = requiredFields.every((field) => {
    const value = watchedValues[field];
    return value && value.toString().trim() !== '';
  });

  const debouncedChangeHandler = useMemo(
    () =>
      debounce((value: string) => {
        if (validateRUT(value)) {
          setValue('rut', transformRUT(value));
        }
      }, 1000),
    [setValue]
  );

  const resetForm = useCallback(() => {
    resetField('business_name');
    resetField('comments');
    resetField('contact_email');
    resetField('contact_name');
    resetField('district');
    resetField('name');
    resetField('number');
    resetField('phone');
    resetField('rut');
    resetField('street');
    resetField('unit');
    resetField('acronym');
    clearBusiness();
    closeDialog(CREATE_BUSINESS_DIALOG);
  }, [closeDialog, resetField, clearBusiness]);

  const onSubmit = useCallback(
    ({ unit = '', comments = '', acronym, commission, ...data }: any) => {
      if (companyId) {
        setLoading(true);

        const businessData = {
          ...data,
          acronym,
          is_provider: true,
          unit,
          comments,
          total_commission: commission,
          areas: [
            {
              acronym: acronym,
              commission: commission,
            },
          ],
        };

        postBusiness(
          companyId,
          businessData,
          () => {
            resetForm();
            getBusinesses(companyId);
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [companyId, postBusiness, resetForm, getBusinesses]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px] md:min-w-[700px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(CREATE_BUSINESS_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear Empresa
              </Typography>
            </div>

            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="flex flex-col md:flex-row gap-24 mb-24">
                    <Controller
                      name="rut"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="RUT"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.rut}
                          helperText={errors?.rut?.message}
                          onChange={(e) => {
                            field.onChange(e);
                            debouncedChangeHandler(e.target.value);
                          }}
                          required
                          fullWidth
                        />
                      )}
                    />

                    <Controller
                      name="acronym"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Sigla (Opcional)"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.acronym}
                          helperText={errors?.acronym?.message || "Opcional: 3 caracteres si se proporciona"}
                          fullWidth
                          inputProps={{
                            maxLength: 3,
                            style: { textTransform: 'uppercase' }
                          }}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            field.onChange(value || undefined);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="business_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Razón Social"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.business_name}
                          helperText={errors?.business_name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="street"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Calle/Avenida"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.street}
                          helperText={errors?.street?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="flex flex-col md:flex-row gap-32 mb-24">
                    <Controller
                      name="number"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Número"
                          type="number"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.number}
                          helperText={errors?.number?.message}
                          required
                          fullWidth
                        />
                      )}
                    />

                    <Controller
                      name="unit"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Oficina/Casa/Otro"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.unit}
                          helperText={errors?.unit?.message}
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <FormControl className="flex" variant="outlined" fullWidth>
                      <InputLabel id="district-label">Comuna *</InputLabel>
                      <Controller
                        name="district"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="district-label"
                            disabled={loading}
                            label="Comuna *"
                            required
                            fullWidth
                            error={!!errors.district}
                          >
                            {DISTRICTS.sort().map((district: string) => (
                              <MenuItem key={district} value={district}>
                                {district}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                    {errors.district && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1, ml: 2 }}
                      >
                        {errors.district.message}
                      </Typography>
                    )}
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Teléfono"
                          type="number"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.phone}
                          helperText={errors?.phone?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="contact_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre de Contacto"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.contact_name}
                          helperText={errors?.contact_name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="contact_email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Email de Contacto"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.contact_email}
                          helperText={errors?.contact_email?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="comments"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Comentarios"
                          variant="outlined"
                          multiline
                          rows={4}
                          disabled={loading}
                          error={!!errors.comments}
                          helperText={errors?.comments?.message}
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  {errorCode && (
                    <Typography color="error" className="text-md text-center mb-8">
                      {errorCode === ERROR.ERR_409 
                        ? 'Ya existe una empresa con esta sigla o RUT' 
                        : errorCode === ERROR.ERR_400
                        ? 'Datos inválidos. Verifique los campos ingresados'
                        : 'Error al crear la empresa. Intente nuevamente'
                      }
                    </Typography>
                  )}

                  <div className="flex justify-center">
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={
                        !isEmpty(errors) || !allRequiredFieldsFilled || loading
                      }
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;
